import _ from 'lodash';
import { Fragment, useState } from 'react';
import { Box, Flex } from 'reflexbox';

import { Avatar, Highlight, Input, KeyBinding, Lazy, Separator } from '@treams/treams-ui';
import { SearchIcon } from '@treams/treams-icons';
import { useKeyboardNavigation } from '@treams/treams-hooks';

import * as graphql from 'generated/graphql';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';
import styled from 'styled-components';

const Result = styled(Flex)<{ first?: boolean; selected?: boolean }>`
	cursor: pointer;

	${(props) => !props.first && `border-top: 1px solid ${props.theme.colors.lightBlueLight};`}
	${(props) => props.selected && `background: ${props.theme.colors.lightBlueLight};`}
`;

export default function UserSelector(props: {
	placeholder?: string;
	me: graphql.User;
	onChange: (user: Partial<graphql.User>) => void;
}) {
	const [query, setQuery] = useState('');

	const { results, loading, version } = useSearch(query, {
		status: [graphql.UserStatus.Active, graphql.UserStatus.Inactive],
	});
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const excluded = results.filter(({ id }: any) => id !== props.me.id);

	const { onKeyDown, selectedIndex, setSelectedIndex } = useKeyboardNavigation({
		count: excluded.length,
		key: version,
		onEnter: (index) => {
			props.onChange(excluded[index]);
		},
	});

	return (
		<Box width={300}>
			<Box p={2}>
				<Input
					placeholder={props.placeholder}
					onChange={setQuery}
					onKeyDown={onKeyDown}
					value={query}
					autoFocus
					small
					icon={<SearchIcon />}
					loading={loading}
				/>
			</Box>
			<Separator />
			<Box overflowY="auto" maxHeight={200}>
				{_.chunk(excluded, 10).map((results, page) => (
					<Lazy key={page} estimatedHeight={results.length * 33 - 1}>
						{results.map((result, i) => (
							<Fragment key={result.id}>
								<Result
									p={1}
									alignItems="center"
									first={i === 0 && page === 0}
									selected={page * 10 + i === selectedIndex}
									onMouseEnter={() => setSelectedIndex(page * 10 + i)}
									onClick={() => props.onChange(result)}
								>
									<Avatar user={result} size={24} />
									<Box ml={2} flexGrow={1} flexShrink={1}>
										<Highlight query={query}>
											{result.firstName} {result.lastName}
										</Highlight>
									</Box>
									{page * 10 + i === selectedIndex && <KeyBinding>Enter ⏎</KeyBinding>}
								</Result>
							</Fragment>
						))}
					</Lazy>
				))}
			</Box>
		</Box>
	);
}
